import React, { useState, useEffect } from 'react';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    AppBar,
    Toolbar,
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    CircularProgress,
    styled
} from '@mui/material';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.hmsaa.com.mx:5001';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
}));

function SoftwareTable() {
    const [software, setSoftware] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [fabricante, setFabricante] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentSoftware, setCurrentSoftware] = useState(null);
    const [errors, setErrors] = useState({});

    const [newSoftware, setNewSoftware] = useState({
        descripcion: '',
        fabricante: '',
        version: ''
    });

    // --- Cargar datos al iniciar ---
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await fetch(`${API_URL}/api/software`);
            if (!response.ok) throw new Error('Error al obtener datos');
            const data = await response.json();
            setSoftware(data);
        } catch (err) {
            console.error('Error al cargar software:', err);
            Swal.fire('Error', 'No se pudieron cargar los datos de software', 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- Validación simple ---
    const validateForm = () => {
        const newErrors = {};
        if (!newSoftware.descripcion.trim()) newErrors.descripcion = 'Requerido';
        if (!newSoftware.fabricante.trim()) newErrors.fabricante = 'Requerido';
        if (!newSoftware.version.trim()) newErrors.version = 'Requerido';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- Crear o actualizar ---
    const handleSaveSoftware = async () => {
        if (!validateForm()) {
            Swal.fire('Error', 'Corrija los campos requeridos', 'error');
            return;
        }

        try {
            const method = editMode ? 'PUT' : 'POST';
            const url = editMode
                ? `${API_URL}/api/software/${currentSoftware.id_software}`
                : `${API_URL}/api/software`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newSoftware),
            });

            if (!response.ok) throw new Error('Error al guardar el registro');

            Swal.fire('Éxito', `Software ${editMode ? 'actualizado' : 'registrado'} correctamente`, 'success');
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            console.error('Error:', err);
            Swal.fire('Error', err.message, 'error');
        }
    };

    // --- Eliminar registro ---
    const handleDeleteSoftware = async (item) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción eliminará el registro de software',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${API_URL}/api/software/${item.id_software}`, {
                    method: 'DELETE',
                });
                if (!response.ok) throw new Error('Error al eliminar');
                Swal.fire('Eliminado', 'El registro ha sido eliminado', 'success');
                setSoftware(software.filter(s => s.id_software !== item.id_software));
            } catch (err) {
                console.error('Error:', err);
                Swal.fire('Error', err.message, 'error');
            }
        }
    };

    // --- Abrir diálogo para crear o editar ---
    const handleAddSoftware = () => {
        setEditMode(false);
        setNewSoftware({ descripcion: '', fabricante: '', version: '' });
        setErrors({});
        setDialogOpen(true);
    };

    const handleEditSoftware = (item) => {
        setEditMode(true);
        setCurrentSoftware(item);
        setNewSoftware({
            descripcion: item.descripcion,
            fabricante: item.fabricante,
            version: item.version
        });
        setErrors({});
        setDialogOpen(true);
    };

    const termino = search.trim().toLowerCase();
    const fabricantes = [...new Set(software.map(s => s.fabricante).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));

    const filteredSoftware = software.filter(s =>
        (!fabricante || s.fabricante === fabricante) &&
        [s.descripcion, s.fabricante, s.version].some(v =>
            (v ?? '').toString().toLowerCase().includes(termino)
        )
    );

    const limpiarFiltros = () => {
        setSearch('');
        setFabricante('');
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={5}>
                <CircularProgress />
                <Typography>Cargando...</Typography>
            </Box>
        );
    }

    return (
        <div>
            {/* --- BARRA SUPERIOR --- */}
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Gestión de Software
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={handleAddSoftware}
                    >
                        Nuevo
                    </Button>
                </Toolbar>
            </AppBar>

            <BarraFiltros
                busqueda={search}
                onBusqueda={setSearch}
                placeholder="Buscar por descripción, fabricante o versión"
                filtros={[
                    {
                        id: 'fabricante',
                        tipo: 'select',
                        etiqueta: 'Fabricante',
                        textoTodos: 'Todos los fabricantes',
                        valor: fabricante,
                        onChange: setFabricante,
                        opciones: fabricantes.map(f => ({ valor: f, etiqueta: f })),
                    },
                ]}
                total={software.length}
                mostrados={filteredSoftware.length}
                onLimpiar={limpiarFiltros}
            />

            {/* --- TABLA DE SOFTWARE --- */}
            <TableContainer component={Paper} sx={{ margin: '20px auto', maxWidth: '90%' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>ID</StyledTableCell>
                            <StyledTableCell>Descripción</StyledTableCell>
                            <StyledTableCell>Fabricante</StyledTableCell>
                            <StyledTableCell>Versión</StyledTableCell>
                            <StyledTableCell>Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredSoftware.map((s) => (
                            <TableRow key={s.id_software}>
                                <TableCell>{s.id_software}</TableCell>
                                <TableCell>{s.descripcion}</TableCell>
                                <TableCell>{s.fabricante}</TableCell>
                                <TableCell>{s.version}</TableCell>
                                <TableCell>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<FontAwesomeIcon icon={faEdit} />}
                                            onClick={() => handleEditSoftware(s)}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={<FontAwesomeIcon icon={faTrash} />}
                                            onClick={() => handleDeleteSoftware(s)}
                                        >
                                            Eliminar
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredSoftware.length === 0 && (
                            <SinResultados colSpan={5} total={software.length} onLimpiar={limpiarFiltros} entidad="programas" />
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* --- DIALOGO NUEVO/EDITAR --- */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
                    {editMode ? 'Editar Software' : 'Nuevo Software'}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        margin="dense"
                        label="Descripción*"
                        fullWidth
                        value={newSoftware.descripcion}
                        onChange={(e) => setNewSoftware({ ...newSoftware, descripcion: e.target.value })}
                        error={!!errors.descripcion}
                        helperText={errors.descripcion}
                    />
                    <TextField
                        margin="dense"
                        label="Fabricante*"
                        fullWidth
                        value={newSoftware.fabricante}
                        onChange={(e) => setNewSoftware({ ...newSoftware, fabricante: e.target.value })}
                        error={!!errors.fabricante}
                        helperText={errors.fabricante}
                    />
                    <TextField
                        margin="dense"
                        label="Versión*"
                        fullWidth
                        value={newSoftware.version}
                        onChange={(e) => setNewSoftware({ ...newSoftware, version: e.target.value })}
                        error={!!errors.version}
                        helperText={errors.version}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button onClick={handleSaveSoftware} variant="contained" color="primary">
                        {editMode ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default SoftwareTable;
